import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collectionService } from "@/services";
import type { CollectionDemand, CycleCollectionView, Member, MemberAccountTransaction } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { FilterBar } from "@/components/feature/FilterBar";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Select, type SelectOption } from "@/components/base/Select";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { HeaderBand } from "./components/HeaderBand";
import { InlinePayCard } from "./components/InlinePayCard";
import { BulkReceiptModal } from "./components/BulkReceiptModal";
import { CollectionSheetPrint } from "./components/CollectionSheetPrint";
import { demandStatusMeta } from "../components/labels";
import { CollectionDepositModal } from "../components/CollectionDepositModal";
import { CollectionCompleteModal } from "../components/CollectionCompleteModal";
import { AccountReceiptModal } from "../../members/profile/components/AccountReceiptModal";
import { ReversePaymentModal, type ReversalPaymentTarget } from "../components/ReversePaymentModal";
import { canReverseCollection } from "@/config/roles";
import { useToast } from "@/context/ToastContext";

export default function CollectionSheetPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { isViewer, role } = useAuth();
  const toast = useToast();
  const canReverse = canReverseCollection(role);

  const [view, setView] = useState<CycleCollectionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<number>(0);
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<CollectionDemand[]>([]);
  const [printMode, setPrintMode] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [autoDeducting, setAutoDeducting] = useState(false);
  const [closingCycle, setClosingCycle] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [reversalTarget, setReversalTarget] = useState<ReversalPaymentTarget | null>(null);

  // Deposit advance modal & receipt modal
  const [depositOpen, setDepositOpen] = useState(false);
  const [receiptTx, setReceiptTx] = useState<MemberAccountTransaction | null>(null);
  const [receiptMember, setReceiptMember] = useState<Member | null>(null);

  const load = useCallback(
    async (cycle?: number) => {
      if (!groupId) return;
      setError(null);
      try {
        const data = await collectionService.getCycleCollection(groupId, cycle);
        setView(data);
        setSelectedCycle(data.cycle);
        setRecordingId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load this collection sheet.");
        setView(null);
      }
    },
    [groupId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const changeCycle = (cycle: number) => {
    load(cycle);
  };

  const onPaid = () => {
    load(selectedCycle);
  };

  const advanceEligibleCount = useMemo(
    () => view?.demands.filter((d) => d.balance > 0 && (d.accountBalance ?? 0) > 0).length ?? 0,
    [view]
  );

  const overdueWithFeeCount = useMemo(
    () =>
      view?.demands.filter(
        (d) => d.status !== "paid" && ((d.applicableLateFee ?? 0) > 0 || (d.lateFee ?? 0) > 0)
      ).length ?? 0,
    [view]
  );

  const pendingDuesCount = useMemo(
    () => view?.demands.filter((d) => d.status !== "paid" && d.balance > 0).length ?? 0,
    [view]
  );

  const isCollectionCompleted = useMemo(() => {
    if (!view || view.auctionPending) return false;
    if (view.balance <= 0) return true;
    if (view.demands && view.demands.length > 0 && pendingDuesCount === 0) return true;
    if (view.status === "CLOSED") return true;
    return false;
  }, [view, pendingDuesCount]);

  const handleAutoDeduct = async () => {
    if (!view?.cycleId) return;
    if (advanceEligibleCount === 0) {
      toast.info("No advance balances", "No members with pending dues have available advance balances for this cycle.");
      return;
    }
    const confirmed = window.confirm(
      `Auto-deduct pending dues from advance balances for ${advanceEligibleCount} member(s)?`
    );
    if (!confirmed) return;

    setAutoDeducting(true);
    try {
      const res = await collectionService.autoDeductCycleAdvance(view.cycleId);
      toast.success(
        "Auto-deduction complete",
        `Deducted ₹${res.totalDeducted.toLocaleString("en-IN")} across ${res.deductions.length} member installment(s).`
      );
      load(selectedCycle);
    } catch (err) {
      toast.error("Auto-deduct failed", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setAutoDeducting(false);
    }
  };

  const handleCloseCycle = async () => {
    if (!view?.cycleId) return;
    const hasUnpaid = view.demands.some((d) => d.balance > 0);
    const confirmed = window.confirm(
      hasUnpaid
        ? `Complete & close Cycle ${view.cycle}? Any unpaid member installment dues will remain on member accounts and carry over to future cycles.`
        : `Complete and close Cycle ${view.cycle}?`
    );
    if (!confirmed) return;

    setClosingCycle(true);
    try {
      await collectionService.closeCycle(view.cycleId);
      toast.success("Cycle completed", `Cycle ${view.cycle} has been marked as closed.`);
      load(selectedCycle);
    } catch (err) {
      toast.error("Could not complete cycle", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setClosingCycle(false);
    }
  };

  const activeFilterCount = [search, status].filter(Boolean).length;

  const filtered = useMemo(() => {
    if (!view) return [];
    let out = view.demands;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (d) =>
          d.memberName.toLowerCase().includes(q) ||
          d.memberPhone.includes(q) ||
          d.ticketNumber.toLowerCase().includes(q) ||
          d.memberCode.toLowerCase().includes(q)
      );
    }
    if (status === "paid") out = out.filter((d) => d.status === "paid");
    else if (status === "partial") out = out.filter((d) => d.status === "partial");
    else if (status === "pending") out = out.filter((d) => d.status === "pending" && d.daysOverdue === 0);
    else if (status === "overdue") out = out.filter((d) => d.status !== "paid" && d.daysOverdue > 0);
    return out;
  }, [view, search, status]);

  const statusOptions: SelectOption[] = [
    { value: "", label: "All statuses" },
    { value: "paid", label: "Paid" },
    { value: "partial", label: "Partial" },
    { value: "pending", label: "Pending" },
    { value: "overdue", label: "Overdue" },
  ];

  const balanceTone = (d: CollectionDemand) =>
    d.status === "paid" ? "text-foreground-400" : d.daysOverdue > 0 ? "text-danger-600" : "text-warning-700";

  const columns: DataTableColumn<CollectionDemand>[] = [
    {
      id: "ticket",
      header: "Ticket",
      accessor: (d) => <span className="tnum text-xs font-semibold text-primary-700">{d.ticketNumber}</span>,
      sortValue: (d) => d.ticketNumber,
      sortable: true,
    },
    {
      id: "member",
      header: "Member",
      accessor: (d) => (
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground-900">{d.memberName}</span>
            {d.wonThisCycle ? <Badge color="accent">Winner</Badge> : null}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <span className="tnum text-xs text-foreground-500">{d.memberPhone}</span>
            {d.accountBalance != null && d.accountBalance > 0 ? (
              <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-success-50 text-success-700 border border-success-200">
                Adv: ₹{d.accountBalance.toLocaleString("en-IN")}
              </span>
            ) : null}
            {d.previousPendingDues && d.previousPendingDues.length > 0 ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-950 border border-amber-300"
                title={`Previous unpaid dues: ₹${(d.totalPreviousPendingDues ?? 0).toLocaleString("en-IN")} across ${d.previousPendingDues.length} earlier cycle(s)`}
              >
                <i className="ri-error-warning-fill text-amber-600 text-xs" />
                Prev Dues: ₹{(d.totalPreviousPendingDues ?? 0).toLocaleString("en-IN")} ({d.previousPendingDues.map((p) => `C${p.cycleNo}`).join(", ")})
              </span>
            ) : null}
          </div>
        </div>
      ),
      sortValue: (d) => d.memberName,
      sortable: true,
    },
    {
      id: "base",
      header: "Base installment",
      align: "right",
      accessor: (d) => <CurrencyDisplay amount={d.baseInstallment} hideSymbol />,
      sortValue: (d) => d.baseInstallment,
      sortable: true,
    },
    {
      id: "dividend",
      header: "Dividend",
      align: "right",
      accessor: (d) =>
        d.dividendCredit > 0 ? (
          <span className="tnum text-success-600">-{d.dividendCredit.toLocaleString("en-IN")}</span>
        ) : (
          <span className="text-foreground-300">—</span>
        ),
      sortValue: (d) => d.dividendCredit,
      sortable: true,
    },
    {
      id: "commission",
      header: "Commission",
      align: "right",
      accessor: (d) =>
        d.commission > 0 ? (
          <span className="tnum font-medium text-primary-700">+{d.commission.toLocaleString("en-IN")}</span>
        ) : (
          <span className="text-foreground-300">—</span>
        ),
      sortValue: (d) => d.commission,
      sortable: true,
      footerValue: (rows) => (
        <CurrencyDisplay amount={rows.reduce((s, d) => s + (d.commission || 0), 0)} hideSymbol />
      ),
    },
    {
      id: "lateFee",
      header: "Late fee",
      align: "right",
      accessor: (d) =>
        d.lateFee > 0 ? (
          <CurrencyDisplay amount={d.lateFee} hideSymbol tone="danger" />
        ) : (d.applicableLateFee ?? 0) > 0 ? (
          <span className="inline-flex flex-col items-end" title="Overdue fee will apply upon payment">
            <span className="text-xs font-medium text-amber-600">
              +₹{(d.applicableLateFee ?? 0).toLocaleString("en-IN")}
            </span>
            <span className="text-[10px] text-foreground-400">overdue</span>
          </span>
        ) : (
          <span className="text-foreground-300">—</span>
        ),
      sortValue: (d) => (d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0)),
      sortable: true,
      footerValue: (rows) => (
        <CurrencyDisplay
          amount={rows.reduce((s, d) => s + (d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0)), 0)}
          hideSymbol
        />
      ),
    },
    {
      id: "net",
      header: "Net payable",
      align: "right",
      accessor: (d) => <CurrencyDisplay amount={d.netPayable} hideSymbol />,
      sortValue: (d) => d.netPayable,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, d) => s + d.netPayable, 0)} hideSymbol />,
    },
    {
      id: "paid",
      header: "Paid",
      align: "right",
      accessor: (d) => <CurrencyDisplay amount={d.paid} hideSymbol tone={d.status === "paid" ? "success" : "default"} />,
      sortValue: (d) => d.paid,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, d) => s + d.paid, 0)} hideSymbol />,
    },
    {
      id: "balance",
      header: "Balance",
      align: "right",
      accessor: (d) => <CurrencyDisplay amount={d.balance} hideSymbol className={balanceTone(d)} />,
      sortValue: (d) => d.balance,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, d) => s + d.balance, 0)} hideSymbol />,
    },
    {
      id: "status",
      header: "Status",
      accessor: (d) => {
        const meta = demandStatusMeta(d.status, d.daysOverdue > 0);
        return <Badge variant={meta.badge} dot>{meta.label}</Badge>;
      },
      sortValue: (d) => d.status,
      sortable: true,
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      width: "215px",
      hideable: false,
      accessor: (d) => (
        <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-end gap-1.5">
          <Button
            size="sm"
            variant={d.balance > 0 ? "primary" : "outline"}
            icon={<i className="ri-add-line" />}
            disabled={isViewer || d.balance <= 0 || recordingId === d.id}
            onClick={() => setRecordingId(d.id)}
          >
            Record
          </Button>
          <Button
            size="sm"
            variant="outline"
            icon={<i className="ri-printer-line" />}
            disabled={!d.paid || d.paid <= 0}
            title={
              d.paid > 0
                ? d.lastReceiptNo
                  ? `Print Receipt (${d.lastReceiptNo})`
                  : "Print Receipt"
                : "No payment recorded yet"
            }
            onClick={() => {
              if (d.lastReceiptId) {
                navigate(`/collection/receipts/${d.lastReceiptId}`);
              } else {
                navigate(`/collection/receipt?memberId=${d.memberId}&groupId=${d.groupId}`);
              }
            }}
          >
            Print
          </Button>
          {canReverse && d.paid > 0 && (d.lastReceiptId || (d.payments && d.payments.length > 0)) ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 px-2"
              icon={<i className="ri-arrow-go-back-line" />}
              title="Delete / Revert Collection Transaction"
              onClick={() => {
                const latestPay = d.payments?.[0];
                const paymentId = latestPay?.paymentId || d.lastReceiptId;
                if (!paymentId) return;
                setReversalTarget({
                  id: paymentId,
                  receiptNumber: latestPay?.receiptNo || d.lastReceiptNo || "RCPT",
                  memberName: d.memberName,
                  memberCode: d.memberCode,
                  amount: latestPay ? Number(latestPay.amount) : d.paid,
                  mode: latestPay?.mode,
                  date: latestPay?.paidAt || latestPay?.createdAt,
                  installmentInfo: `${d.groupName} · Cycle ${d.cycle}`,
                });
              }}
            >
              Revert
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate("/collection")}>
      Back
    </Button>
  );

  const breadcrumb = view ? (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Collection" to="/collection" />
      <BreadcrumbItem label={`${view.groupName} · Cycle ${view.cycle}`} isLast />
    </>
  ) : (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Collection" to="/collection" />
      <BreadcrumbItem label="Collection sheet" isLast />
    </>
  );

  if (error) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Collection sheet" actions={back} />
        <Card><ErrorState message={error} onRetry={() => load(selectedCycle)} /></Card>
      </div>
    );
  }

  if (!view) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Loading collection sheet…" actions={back} />
        <div className="h-40 animate-pulse rounded-lg bg-background-200" />
      </div>
    );
  }

  if (printMode) {
    return (
      <div>
        <PageHeader
          className="no-print"
          breadcrumb={breadcrumb}
          title="Collection sheet"
          actions={
            <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => setPrintMode(false)}>
              Back to sheet
            </Button>
          }
        />
        <CollectionSheetPrint view={view} />
      </div>
    );
  }

  const recording = recordingId ? view.demands.find((d) => d.id === recordingId) ?? null : null;

  return (
    <div>
      <PageHeader
        breadcrumb={breadcrumb}
        title={view.groupName}
        subtitle={`${view.periodLabel} · Collection sheet`}
        actions={
          <>
            <Button variant="outline" size="sm" icon={<i className="ri-printer-line" />} onClick={() => setPrintMode(true)}>
              Print sheet
            </Button>
            {/* <Button
              variant="outline"
              size="sm"
              icon={<i className="ri-timer-flash-line" />}
              className="border-amber-600 text-amber-700 hover:bg-amber-50 focus-visible:outline-amber-600 font-semibold"
              onClick={() => navigate(`/collection/late-fees?groupId=${view.groupId}`)}
              title={`Manage and apply late fees for ${view.groupName}`}
            >
              Late Fees {overdueWithFeeCount > 0 ? `(${overdueWithFeeCount})` : ""}
            </Button> */}
            <Button
              variant="outline"
              size="sm"
              icon={<i className="ri-hand-coin-line" />}
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 focus-visible:outline-emerald-600 font-semibold"
              disabled={isViewer}
              onClick={() => setDepositOpen(true)}
            >
              + Add deposit
            </Button>
            {isCollectionCompleted ? (
              <Button
                variant="outline"
                size="sm"
                icon={<i className="ri-checkbox-circle-fill text-success-600" />}
                className="border-success-500 bg-success-50 text-success-800 font-semibold hover:bg-success-100 focus-visible:outline-success-600"
                disabled={isViewer || !view || view.auctionPending}
                title="All dues for this cycle are already collected (100%)"
                onClick={() => {
                  toast.info(
                    "Collection Completed",
                    `All member installments for Cycle ${view?.cycle} are fully collected (100%).`
                  );
                }}
              >
                Collection Completed
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                icon={<i className="ri-checkbox-circle-line" />}
                disabled={isViewer || !view || view.auctionPending}
                loading={closingCycle}
                title={
                  pendingDuesCount > 0
                    ? `Complete cycle & settle ${pendingDuesCount} pending dues via Advance Wallets`
                    : "All dues for this cycle are already collected (100%)"
                }
                onClick={() => {
                  if (pendingDuesCount > 0) {
                    setCompleteModalOpen(true);
                  } else {
                    handleCloseCycle();
                  }
                }}
              >
                Complete Cycle {pendingDuesCount > 0 ? `(${pendingDuesCount})` : ""}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon={<i className="ri-file-list-3-line" />}
              disabled={bulkSelected.length === 0 || isViewer}
              onClick={() => setBulkOpen(true)}
            >
              Bulk receipt ({bulkSelected.length})
            </Button>
          </>
        }
      />

      <div className="space-y-4">
        <HeaderBand view={view} selectedCycle={selectedCycle} onCycleChange={changeCycle} />

        {view.auctionPending ? (
          <Alert variant="info" title="No demands for this cycle yet">
            This cycle's auction has not yet been held, so no installment demands exist. Choose an earlier cycle from the
            selector to collect arrears, or return to the auction console once the auction is decided.
          </Alert>
        ) : null}

        {!view.auctionPending ? (
          <>
            <FilterBar
              search={{ value: search, onChange: setSearch, placeholder: "Search name, phone or ticket…" }}
              filters={[{ id: "status", control: <Select value={status} onChange={setStatus} options={statusOptions} /> }]}
              activeCount={activeFilterCount}
              onClear={() => {
                setSearch("");
                setStatus("");
              }}
            />
            <DataTable<CollectionDemand>
              columns={columns}
              rows={filtered}
              rowKey={(d) => d.id}
              emptyMessage="No members to show"
              emptyDescription="Adjust your filters, or record payments for this cycle."
              selectable
              onSelectionChange={(rows) => setBulkSelected(rows)}
              showTotals
              totalLabel="Totals"
              defaultPageSize={25}
              showDensityToggle
              showColumnVisibility={false}
            />
            {recording ? (
              <Card title={`Record payment — ${recording.memberName}`}>
                <InlinePayCard demand={recording} onDone={onPaid} />
              </Card>
            ) : null}
          </>
        ) : null}
      </div>

      <BulkReceiptModal
        open={bulkOpen}
        demands={bulkSelected}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          setBulkOpen(false);
          setBulkSelected([]);
          onPaid();
        }}
      />

      {/* Advance Deposit Modal */}
      {depositOpen ? (
        <CollectionDepositModal
          open
          onClose={() => setDepositOpen(false)}
          onSuccess={(tx, member) => {
            load(selectedCycle);
            setReceiptTx(tx);
            setReceiptMember(member);
          }}
        />
      ) : null}

      {/* Account Receipt Slip Modal */}
      {receiptTx && receiptMember ? (
        <AccountReceiptModal
          open
          onClose={() => {
            setReceiptTx(null);
            setReceiptMember(null);
          }}
          member={receiptMember}
          transaction={receiptTx}
        />
      ) : null}

      {/* Complete Cycle Collection / Settle from Wallet Modal */}
      {view?.cycleId ? (
        <CollectionCompleteModal
          open={completeModalOpen}
          onClose={() => setCompleteModalOpen(false)}
          cycleId={view.cycleId}
          onSuccess={() => load(selectedCycle)}
        />
      ) : null}

      {/* Delete / Revert Collection Transaction Modal */}
      <ReversePaymentModal
        open={Boolean(reversalTarget)}
        target={reversalTarget}
        onClose={() => setReversalTarget(null)}
        onSuccess={() => load(selectedCycle)}
      />
    </div>
  );
}